import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Email as EmailIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudDownload as ExtractIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'aol.com', 'icloud.com', 'protonmail.com', 'mail.com',
  'live.com', 'msn.com', 'ymail.com', 'googlemail.com',
  'yandex.com', 'zoho.com', 'inbox.com', 'gmx.com',
]);

const EmailDomainsManager = ({ organizationId, organization, canEdit, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [extractedDomains, setExtractedDomains] = useState([]);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const verifiedDomains = organization?.verified_email_domains_list || [];

  // console.log("****", organization);

  const handleExtractDomains = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosService.get(
        `/consortium/organizations/${organizationId}/extract-email-domains/`
      );
      setExtractedDomains(
        (response.data.extracted_domains || []).filter(d => !PERSONAL_EMAIL_DOMAINS.has(d.toLowerCase()))
      );
      setExtractDialogOpen(true);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to extract email domains';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (domain = null) => {
    const domainToAdd = (domain || newDomain).trim().toLowerCase();
    if (!domainToAdd) return;

    if (PERSONAL_EMAIL_DOMAINS.has(domainToAdd)) {
      setError(`"${domainToAdd}" is a personal email provider and cannot be used as an organization domain.`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await axiosService.post(
        `/consortium/organizations/${organizationId}/add-email-domain/`,
        { domain: domainToAdd }
      );
      setSuccess(response.data.message);
      setNewDomain('');
      if (onUpdate) onUpdate();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to add email domain';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Are you sure you want to remove ${domain}?`)) return;

    try {
      setLoading(true);
      setError('');
      const response = await axiosService.delete(
        `/consortium/organizations/${organizationId}/remove-email-domain/`,
        { data: { domain } }
      );
      setSuccess(response.data.message);
      if (onUpdate) onUpdate();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to remove email domain';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExtractedDomain = async (domain) => {
    await handleAddDomain(domain);
    setExtractedDomains(extractedDomains.filter(d => d !== domain));
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <EmailIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Verified Email Domains
          </Typography>
          {canEdit && organization?.website && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExtractIcon />}
              onClick={handleExtractDomains}
              disabled={loading}
            >
              Extract from Website
            </Button>
          )}
        </Box>

        {/* <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Email domains used for automated email-based membership verification. 
          Supports exact domains, wildcards (*.edu, university.*), and patterns.
        </Alert> */}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Add new domain */}
        {canEdit && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Email Domain or Pattern"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="e.g., company.com, *.university.edu, university.*"
                size="small"
                fullWidth
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddDomain();
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddDomain()}
                disabled={!newDomain.trim() || loading}
              >
                Add
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Examples: company.com, *.university.edu, university.*, &#123;org&#125;.edu
            </Typography>
          </Box>
        )}

        {/* Current domains */}
        {verifiedDomains.length === 0 ? (
          <Alert severity="info">
            No email domains configured yet. {canEdit && 'Add domains to enable email-based verification.'}
          </Alert>
        ) : (
          <Stack spacing={1}>
            {verifiedDomains.map((domain, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Chip
                  label={domain}
                  color="primary"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace',
                   }}
                />
                {canEdit && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveDomain(domain)}
                    disabled={loading}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </CardContent>

      {/* Extract Dialog */}
      <Dialog open={extractDialogOpen} onClose={() => setExtractDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Extracted Email Domains
          <Typography variant="body2" color="text.secondary">
            From: {organization?.website}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {extractedDomains.length === 0 ? (
            <Alert severity="info">
              No email domains found on the website. You can manually add domains below.
            </Alert>
          ) : (
            <List>
              {extractedDomains.map((domain, index) => (
                <ListItem
                  key={index}
                  divider
                  sx={{
                    bgcolor: verifiedDomains.includes(domain) ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={domain}
                    secondary={verifiedDomains.includes(domain) ? 'Already added' : 'Click to add'}
                    primaryTypographyProps={{ fontFamily: 'monospace' }}
                  />
                  <ListItemSecondaryAction>
                    {verifiedDomains.includes(domain) ? (
                      <Chip label="Added" color="success" size="small" />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddExtractedDomain(domain)}
                        disabled={loading}
                      >
                        Add
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtractDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default EmailDomainsManager;
